import type { StateGS } from "./StateGS";

interface StateHeadTagGS {
    readonly key: string;
    readonly tagName: "meta" | "link" | "script";
    readonly attributes: Readonly<Record<string, string>>;
    readonly textContent: string;
}

interface ShellHeadTagSnapshotGS {
    readonly tagName: StateHeadTagGS["tagName"];
    readonly attributes: Readonly<Record<string, string>>;
    readonly textContent: string;
}

const managedAttribute = "data-gs-head";
const shellDefaultAttribute = "data-gs-head-default";
const internalKeyPrefix = "gs:";
const tagsByState = new WeakMap<StateGS, Map<string, StateHeadTagGS>>();

export function setStateHeadTagGS(
    state: StateGS,
    key: string,
    tagName: "meta" | "link" | "script",
    attributes: Readonly<Record<string, string>>,
    textContent: string
): void {
    const normalizedKey = normalizeKey(key);
    if (normalizedKey.startsWith(internalKeyPrefix))
        throw new Error(`Head tag key '${key}' uses the reserved '${internalKeyPrefix}' prefix.`);

    const normalizedTagName = tagName.toLowerCase() as StateHeadTagGS["tagName"];
    if (normalizedTagName !== "meta" && normalizedTagName !== "link" && normalizedTagName !== "script")
        throw new Error("State head tags support only meta, link and JSON-LD script elements.");

    const normalizedAttributes = normalizeAttributes(attributes);
    validateReservedMetadata(normalizedTagName, normalizedAttributes);
    if (normalizedTagName === "script") {
        if (normalizedAttributes.type?.toLowerCase() !== "application/ld+json" || normalizedAttributes.src)
            throw new Error("State script head tags support only inline application/ld+json content.");
    }

    let tags = tagsByState.get(state);
    if (!tags) {
        tags = new Map<string, StateHeadTagGS>();
        tagsByState.set(state, tags);
    }
    tags.set(normalizedKey, {
        key: normalizedKey,
        tagName: normalizedTagName,
        attributes: normalizedAttributes,
        textContent: String(textContent ?? "")
    });
}

export function removeStateHeadTagGS(state: StateGS, key: string): void {
    tagsByState.get(state)?.delete(normalizeKey(key));
}

/** Apply title, built-in metadata and custom state tags to a browser or server document. */
export function applyStateHeadGS(document: Document, state: StateGS, defaultCanonicalUrl: string): void {
    document.title = state.title;

    const desired = new Map<string, StateHeadTagGS>();
    if (state.description) desired.set("gs:description", {
        key: "gs:description",
        tagName: "meta",
        attributes: { name: "description", content: state.description },
        textContent: ""
    });
    if (state.robots) desired.set("gs:robots", {
        key: "gs:robots",
        tagName: "meta",
        attributes: { name: "robots", content: state.robots },
        textContent: ""
    });

    const canonicalUrl = removeUrlFragment(state.canonicalUrl || defaultCanonicalUrl);
    if (canonicalUrl) desired.set("gs:canonical", {
        key: "gs:canonical",
        tagName: "link",
        attributes: { rel: "canonical", href: canonicalUrl },
        textContent: ""
    });

    for (const tag of tagsByState.get(state)?.values() ?? []) desired.set(tag.key, tag);

    if (!desired.has("gs:description")) removeKnownElements(document, "meta", "name", "description");
    if (!desired.has("gs:robots")) removeKnownElements(document, "meta", "name", "robots");
    if (!desired.has("gs:canonical")) removeKnownElements(document, "link", "rel", "canonical");

    const claimedKeys = new Set<string>();
    for (const element of Array.from(document.head.querySelectorAll(`[${managedAttribute}]`))) {
        const key = normalizeExistingKey(element.getAttribute(managedAttribute));
        const descriptor = key ? desired.get(key) : undefined;
        if (!descriptor || element.localName.toLowerCase() !== descriptor.tagName) {
            restoreShellDefaultOrRemove(element, key);
            continue;
        }
        if (claimedKeys.has(key)) {
            element.remove();
            continue;
        }
        applyDescriptor(element, descriptor);
        claimedKeys.add(key);
        desired.delete(key);
    }

    for (const descriptor of desired.values()) {
        const reusable = findReusableElement(document, descriptor);
        if (reusable && !descriptor.key.startsWith(internalKeyPrefix)) captureShellDefault(reusable);
        const element = reusable ?? document.createElement(descriptor.tagName);
        applyDescriptor(element, descriptor);
        if (!element.parentElement) document.head.append(element);
    }
}

/** Adopt server-managed metadata so BrowserFromServer does not discard dynamic SSR head values. */
export function adoptStateHeadGS(document: Document, state: StateGS): void {
    state.title = document.title;
    for (const element of Array.from(document.head.querySelectorAll(`[${managedAttribute}]`))) {
        const key = normalizeExistingKey(element.getAttribute(managedAttribute));
        if (!key) continue;
        if (key === "gs:description") {
            state.description = element.getAttribute("content") ?? "";
            continue;
        }
        if (key === "gs:robots") {
            state.robots = element.getAttribute("content") ?? "";
            continue;
        }
        if (key === "gs:canonical") {
            state.canonicalUrl = element.getAttribute("href") ?? "";
            continue;
        }
        if (key.startsWith(internalKeyPrefix)) continue;

        const tagName = element.localName.toLowerCase();
        if (tagName !== "meta" && tagName !== "link" && tagName !== "script") continue;
        const attributes: Record<string, string> = {};
        for (const attribute of Array.from(element.attributes)) {
            const attributeName = attribute.name.toLowerCase();
            if (attributeName !== managedAttribute && attributeName !== shellDefaultAttribute)
                attributes[attribute.name.toLowerCase()] = attribute.value;
        }
        setStateHeadTagGS(
            state,
            key,
            tagName,
            attributes,
            element.textContent ?? ""
        );
    }
}

function normalizeKey(key: string): string {
    const normalized = String(key ?? "").trim().toLowerCase();
    if (!normalized) throw new Error("State head tag key must not be empty.");
    return normalized;
}

function normalizeExistingKey(key: string | null): string {
    return String(key ?? "").trim().toLowerCase();
}

function normalizeAttributes(attributes: Readonly<Record<string, string>>): Readonly<Record<string, string>> {
    const normalized: Record<string, string> = {};
    for (const [rawName, rawValue] of Object.entries(attributes ?? {})) {
        const name = rawName.trim().toLowerCase();
        if (!name || !/^[^\s"'<>\/=]+$/.test(name))
            throw new Error(`Invalid state head tag attribute '${rawName}'.`);
        if (name === managedAttribute || name === shellDefaultAttribute)
            throw new Error(`The '${name}' attribute is reserved by GS.`);
        normalized[name] = String(rawValue ?? "");
    }
    return normalized;
}

function validateReservedMetadata(
    tagName: StateHeadTagGS["tagName"],
    attributes: Readonly<Record<string, string>>
): void {
    const metaName = attributes.name?.toLowerCase();
    if (tagName === "meta" && (metaName === "description" || metaName === "robots" || metaName?.startsWith("gs-")))
        throw new Error(`Meta name '${attributes.name}' is managed by StateGS fields or the GS shell.`);
    if (tagName === "link" && attributes.rel?.toLowerCase() === "canonical")
        throw new Error("Canonical links are managed by StateGS.canonicalUrl.");
}

function applyDescriptor(element: Element, descriptor: StateHeadTagGS): void {
    const shellDefault = descriptor.key.startsWith(internalKeyPrefix)
        ? null
        : element.getAttribute(shellDefaultAttribute);
    for (const attribute of Array.from(element.attributes)) element.removeAttribute(attribute.name);
    element.setAttribute(managedAttribute, descriptor.key);
    if (shellDefault !== null) element.setAttribute(shellDefaultAttribute, shellDefault);
    for (const [name, value] of Object.entries(descriptor.attributes)) element.setAttribute(name, value);
    element.textContent = descriptor.tagName === "script"
        ? descriptor.textContent.replace(/<\/script/gi, "<\\/script")
        : descriptor.textContent;
}

function captureShellDefault(element: Element): void {
    const attributes: Record<string, string> = {};
    for (const attribute of Array.from(element.attributes)) {
        const name = attribute.name.toLowerCase();
        if (name !== managedAttribute && name !== shellDefaultAttribute)
            attributes[name] = attribute.value;
    }
    const snapshot: ShellHeadTagSnapshotGS = {
        tagName: element.localName.toLowerCase() as StateHeadTagGS["tagName"],
        attributes,
        textContent: element.textContent ?? ""
    };
    element.setAttribute(shellDefaultAttribute, JSON.stringify(snapshot));
}

function restoreShellDefaultOrRemove(element: Element, key: string): void {
    if (!key.startsWith(internalKeyPrefix) && restoreShellDefault(element)) return;
    element.remove();
}

function restoreShellDefault(element: Element): boolean {
    const serialized = element.getAttribute(shellDefaultAttribute);
    if (!serialized) return false;
    try {
        const snapshot = JSON.parse(serialized) as Partial<ShellHeadTagSnapshotGS>;
        const tagName = snapshot.tagName?.toLowerCase();
        if (tagName !== element.localName.toLowerCase()
            || (tagName !== "meta" && tagName !== "link" && tagName !== "script")
            || !snapshot.attributes
            || Array.isArray(snapshot.attributes)
            || typeof snapshot.attributes !== "object"
            || typeof snapshot.textContent !== "string")
            return false;

        const attributes = normalizeAttributes(snapshot.attributes);
        for (const attribute of Array.from(element.attributes)) element.removeAttribute(attribute.name);
        for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
        element.textContent = snapshot.textContent;
        return true;
    } catch {
        return false;
    }
}

function findReusableElement(document: Document, descriptor: StateHeadTagGS): Element | undefined {
    const candidates = Array.from(document.head.getElementsByTagName(descriptor.tagName))
        .filter(element => !element.hasAttribute(managedAttribute));

    if (descriptor.key === "gs:description")
        return candidates.find(element => attributeEquals(element, "name", "description"));
    if (descriptor.key === "gs:robots")
        return candidates.find(element => attributeEquals(element, "name", "robots"));
    if (descriptor.key === "gs:canonical")
        return candidates.find(element => attributeEquals(element, "rel", "canonical"));

    const id = descriptor.attributes.id;
    if (id) return candidates.find(element => element.getAttribute("id") === id);
    if (descriptor.tagName === "meta") {
        for (const identity of ["name", "property", "http-equiv"]) {
            const value = descriptor.attributes[identity];
            if (value !== undefined)
                return candidates.find(element => element.getAttribute(identity) === value);
        }
    }
    return undefined;
}

function removeKnownElements(document: Document, tagName: string, attribute: string, value: string): void {
    for (const element of Array.from(document.head.getElementsByTagName(tagName))) {
        if (attributeEquals(element, attribute, value)) element.remove();
    }
}

function attributeEquals(element: Element, name: string, value: string): boolean {
    return element.getAttribute(name)?.toLowerCase() === value.toLowerCase();
}

function removeUrlFragment(url: string): string {
    const fragmentIndex = url.indexOf("#");
    return fragmentIndex < 0 ? url : url.slice(0, fragmentIndex);
}
